import React from "react";
import { Match, Player } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";
import { sameTeamId } from "../../utils/teamId";

/**
 * Roster to offer for a given event's team_id — decision 5 (2026-08-09
 * client wave): the FULL roster of the event's OWN team, not on-pitch-only.
 * Corrections happen minutes later, after substitutions have already
 * changed who is on the pitch. Falls back to the combined roster for a
 * neutral/unresolvable team_id, mirroring ReviewView.tsx:231-236's
 * `getTeamPlayers`. Shared by LiveEventFeed's inline player editor and
 * TeamSelector's "change last event player" quick action so both stay on
 * one roster-resolution rule.
 */
export const getTeamRosterForEvent = (
  teamId: string | null | undefined,
  match: Match | null,
): Player[] => {
  if (!match) return [];
  if (sameTeamId(teamId, match.home_team.id)) return match.home_team.players;
  if (sameTeamId(teamId, match.away_team.id)) return match.away_team.players;
  return [...match.home_team.players, ...match.away_team.players];
};

export interface PlayerPickerProps {
  players: Player[];
  value: string;
  onChange: (playerId: string) => void;
  t: any;
  testId?: string;
  disabled?: boolean;
  className?: string;
  /**
   * When true, omits the "no player" option — the picker always resolves
   * to a real player_id. Used by flows (e.g. the quick "change last event
   * player" action) where a player must always be selected. Defaults to
   * false, matching ReviewView.tsx's original player <select> (L486-502),
   * which allows clearing an event back to a team-level event.
   */
  requirePlayer?: boolean;
}

/**
 * Shared player-select control. Mirrors ReviewView.tsx's L486-502 player
 * dropdown shape (jersey number + display name) so the two screens read
 * identically to the operator. Reused by:
 * - LiveEventFeed.tsx's inline player editor (PMA-COCK-099)
 * - TeamSelector.tsx's "change last event player" quick action
 *
 * Roster scope (which players are passed in) is the caller's
 * responsibility — this component only renders whatever list it is given.
 */
export const PlayerPicker: React.FC<PlayerPickerProps> = ({
  players,
  value,
  onChange,
  t,
  testId,
  disabled,
  className,
  requirePlayer = false,
}) => {
  return (
    <select
      data-testid={testId}
      disabled={disabled}
      className={
        className ??
        "w-full text-xs bg-slate-800 text-slate-200 border border-slate-700 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
      }
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {!requirePlayer && (
        <option value="">{t("playerPickerTeamEvent", "Team Event")}</option>
      )}
      {players.map((p) => (
        <option key={p.id} value={p.id}>
          #{p.jersey_number} {formatDisplayName(p.full_name)}
        </option>
      ))}
    </select>
  );
};

export default PlayerPicker;
